import { FaFunction } from '../../common'
import * as calculations from '../calculations'
import { Report } from '../report'

// TODO: each Exchange and PoolSummary report is very similar. If the customer
// approves, we should standardize them so that we can significantly reduce the
// amount of the code in this file

// TODO: come up with a way to have IDC function names be pulled at runtime
// (e.g. `Allocation of ${CurrentNameForSAS} Cost Pool`)

/**
 * Generates and returns the default list of {@link Report.Label}
 */
export function getDefaultReportLabels({
  directFaCategories,
}: Readonly<getDefaultReportLabels.Params>): readonly Report.Label[] {
  return (
    [
      ...directFaCategories.map<ReportLabelEssentials>((faCategory, i) => ({
        // 65 = 'A'
        code: `1${String.fromCharCode(65 + i)}`,
        faFunctionCategorization: {
          categoryId: faCategory.id,
        },
        id: Report.Id.create({
          extra: faCategory.id,
          type: Report.Type.RateSummary,
        }),
        name: `Facilities & Administrative Rate Summary - ${faCategory.name}`,
      })),
      {
        code: '2',
        id: Report.Id.create({ type: Report.Type.AllAllocationsSummary }),
        name: 'Stepdown Allocation to Direct Cost Objectives',
      },
      {
        code: '3B',
        id: Report.Id.create({ type: Report.Type.AllPoolMappingsSummary }),
        name: 'Reclassifications & Adjustments',
      },
      /* -------------------------- Building Depreciation ------------------------- */
      ...(
        [
          {
            code: '6',
            id: Report.Id.create({
              extra: FaFunction.Category.Standard.Id.of('BuildingDepreciation'),
              type: Report.Type.Exchange,
            }),
            name: 'Allocation of Building Depreciation',
          },
          {
            code: '6A',
            id: Report.Id.create({
              extra: FaFunction.Category.Standard.Id.of('BuildingDepreciation'),
              type: Report.Type.PoolSummary,
            }),
            name: 'Schedule of Allowable Building Depreciation',
          },
          {
            code: '6B',
            id: Report.Id.create({
              extra: {
                categoryId: FaFunction.Category.Standard.Id.of(
                  'BuildingDepreciation',
                ),
                valueType: calculations.Allocation.ValueType.Currency,
              },
              type: Report.Type.AllocationSupport,
            }),
            name: 'Allocation of Building Depreciation',
          },
          {
            code: '6C',
            id: Report.Id.create({
              extra: {
                categoryId: FaFunction.Category.Standard.Id.of(
                  'BuildingDepreciation',
                ),
                valueType: calculations.Allocation.ValueType.Statistic,
              },
              type: Report.Type.AllocationSupport,
            }),
            name: 'Allocation of Building Depreciation - Sq Ft by Building',
          },
          {
            code: '6D',
            id: Report.Id.create({
              extra: {
                categoryId: FaFunction.Category.Standard.Id.of(
                  'BuildingDepreciation',
                ),
                valueType: calculations.Allocation.ValueType.Percent,
              },
              type: Report.Type.AllocationSupport,
            }),
            name: 'Allocation of Building Depreciation - Allocation Statistics',
          },
        ] satisfies readonly Pick<
          ReportLabelEssentials,
          'code' | 'id' | 'name'
        >[]
      ).map<ReportLabelEssentials>(partialLabel => ({
        ...partialLabel,
        faFunctionCategorization: {
          categoryId: FaFunction.Category.Standard.Id.of(
            'BuildingDepreciation',
          ),
        },
      })),
      /* ------------------------- Equipment Depreciation ------------------------- */
      ...(
        [
          {
            code: '7',
            id: Report.Id.create({
              extra: FaFunction.Category.Standard.Id.of(
                'EquipmentDepreciation',
              ),
              type: Report.Type.Exchange,
            }),
            name: 'Allocation of Equipment Depreciation',
          },
          {
            code: '7A',
            id: Report.Id.create({
              extra: FaFunction.Category.Standard.Id.of(
                'EquipmentDepreciation',
              ),
              type: Report.Type.PoolSummary,
            }),
            name: 'Schedule of Equipment Depreciation Cost',
          },
          {
            code: '7B',
            id: Report.Id.create({
              extra: FaFunction.Category.Standard.Id.of(
                'EquipmentDepreciation',
              ),
              type: Report.Type.SubpoolAllocationsSummary,
            }),
            name: 'Summary of Allocation of Equipment Depreciation by Method',
          },
          ...[
            {
              codeSuffix: 'C',
              entityName: 'Room',
              subcategoryCode: FaFunction.Subcategory.Standard.Code.Room,
            },
            {
              codeSuffix: 'D',
              entityName: 'Department',
              subcategoryCode: FaFunction.Subcategory.Standard.Code.Department,
            },
            {
              codeSuffix: 'E',
              entityName: 'Building',
              subcategoryCode: FaFunction.Subcategory.Standard.Code.Building,
            },
            {
              codeSuffix: 'F',
              entityName: 'Campus',
              subcategoryCode: FaFunction.Subcategory.Standard.Code.Campus,
            },
          ].map<
            Pick<ReportLabelEssentials, 'code' | 'id' | 'name'> & {
              subcategoryCode?: FaFunction.Subcategory.Standard.Code
            }
          >(({ codeSuffix, entityName, subcategoryCode }) => ({
            code: `7${codeSuffix}`,
            id: Report.Id.create({
              extra: {
                categoryId: FaFunction.Category.Standard.Id.of(
                  'EquipmentDepreciation',
                ),
                subcategoryId: FaFunction.Subcategory.Standard.Id.create(
                  FaFunction.Category.Standard.Code.EquipmentDepreciation,
                  subcategoryCode,
                ),
                valueType: calculations.Allocation.ValueType.Currency,
              },
              type: Report.Type.AllocationSupport,
            }),
            name: `Allocation of Equipment Depreciation by ${entityName}`,
            subcategoryCode,
          })),
        ] satisfies readonly Pick<
          ReportLabelEssentials,
          'code' | 'id' | 'name'
        >[]
      ).map<ReportLabelEssentials>(partialLabel => ({
        ...partialLabel,
        faFunctionCategorization: {
          categoryId: FaFunction.Category.Standard.Id.of(
            'EquipmentDepreciation',
          ),
          subcategoryId: partialLabel.subcategoryCode
            ? FaFunction.Subcategory.Standard.Id.create(
                FaFunction.Category.Standard.Code.EquipmentDepreciation,
                partialLabel.subcategoryCode,
              )
            : undefined,
        },
      })),
      /* -------------------------------- Interest -------------------------------- */
      ...(
        [
          {
            code: '8',
            id: Report.Id.create({
              extra: FaFunction.Category.Standard.Id.of('Interest'),
              type: Report.Type.Exchange,
            }),
            name: 'Allocation of Interest Cost Pool',
          },
          {
            code: '8A',
            id: Report.Id.create({
              extra: FaFunction.Category.Standard.Id.of('Interest'),
              type: Report.Type.PoolSummary,
            }),
            name: 'Schedule of Allowable Interest Expense',
          },
          {
            code: '8B',
            id: Report.Id.create({
              extra: {
                categoryId: FaFunction.Category.Standard.Id.of('Interest'),
                valueType: calculations.Allocation.ValueType.Currency,
              },
              type: Report.Type.AllocationSupport,
            }),
            name: 'Allocation of Interest Expense',
          },
          {
            code: '8C',
            id: Report.Id.create({
              extra: {
                categoryId: FaFunction.Category.Standard.Id.of('Interest'),
                valueType: calculations.Allocation.ValueType.Statistic,
              },
              type: Report.Type.AllocationSupport,
            }),
            name: 'Allocation of Interest Expense - Sq Ft by Building',
          },
          {
            code: '8D',
            id: Report.Id.create({
              extra: {
                categoryId: FaFunction.Category.Standard.Id.of('Interest'),
                valueType: calculations.Allocation.ValueType.Percent,
              },
              type: Report.Type.AllocationSupport,
            }),
            name: 'Allocation of Interest Expense - Allocation Statistics',
          },
        ] satisfies readonly Pick<
          ReportLabelEssentials,
          'code' | 'id' | 'name'
        >[]
      ).map<ReportLabelEssentials>(partialLabel => ({
        ...partialLabel,
        faFunctionCategorization: {
          categoryId: FaFunction.Category.Standard.Id.of('Interest'),
        },
      })),
      /* ------------------------ Operations & Maintenance ------------------------ */
      ...(
        [
          {
            code: '9',
            id: Report.Id.create({
              extra: FaFunction.Category.Standard.Id.of(
                'OperationsAndMaintenance',
              ),
              type: Report.Type.Exchange,
            }),
            name: 'Allocation of O&M Cost Pool',
          },
          {
            code: '9A',
            id: Report.Id.create({
              extra: FaFunction.Category.Standard.Id.of(
                'OperationsAndMaintenance',
              ),
              type: Report.Type.PoolSummary,
            }),
            name: 'Summary of Operations & Maintenance Cost Pool',
          },
          {
            code: '9B',
            id: Report.Id.create({
              extra: FaFunction.Category.Standard.Id.of(
                'OperationsAndMaintenance',
              ),
              type: Report.Type.SubpoolAllocationsSummary,
            }),
            name: 'Operations & Maintenance Allocation Summary',
          },
          {
            code: '9C',
            id: Report.Id.create({
              extra: {
                categoryId: FaFunction.Category.Standard.Id.of(
                  'OperationsAndMaintenance',
                ),
                valueType: calculations.Allocation.ValueType.Currency,
              },
              type: Report.Type.AllocationSupport,
            }),
            name: 'Allocation of Operations & Maintenance Cost',
          },
          {
            code: '9D',
            id: Report.Id.create({
              extra: {
                categoryId: FaFunction.Category.Standard.Id.of(
                  'OperationsAndMaintenance',
                ),
                valueType: calculations.Allocation.ValueType.Statistic,
              },
              type: Report.Type.AllocationSupport,
            }),
            name: 'Operations & Maintenance Cost Pool Allocation Statistics',
          },
          {
            code: '9E',
            id: Report.Id.create({
              extra: {
                categoryId: FaFunction.Category.Standard.Id.of(
                  'OperationsAndMaintenance',
                ),
                valueType: calculations.Allocation.ValueType.Percent,
              },
              type: Report.Type.AllocationSupport,
            }),
            name: 'Operations & Maintenance Cost Pool Allocation Percentages',
          },
        ] satisfies readonly Pick<
          ReportLabelEssentials,
          'code' | 'id' | 'name'
        >[]
      ).map<ReportLabelEssentials>(partialLabel => ({
        ...partialLabel,
        faFunctionCategorization: {
          categoryId: FaFunction.Category.Standard.Id.of(
            'OperationsAndMaintenance',
          ),
        },
      })),
      /* ------------------------- General Administration ------------------------- */
      ...(
        [
          {
            code: '10',
            id: Report.Id.create({
              extra: FaFunction.Category.Standard.Id.of(
                'GeneralAdministration',
              ),
              type: Report.Type.Exchange,
            }),
            name: 'Allocation of General Administration Cost Pool',
          },
          {
            code: '10A',
            id: Report.Id.create({
              extra: FaFunction.Category.Standard.Id.of(
                'GeneralAdministration',
              ),
              type: Report.Type.PoolSummary,
            }),
            name: 'General Administration & General Expenses',
          },
          {
            code: '10B',
            id: Report.Id.create({
              extra: FaFunction.Category.Standard.Id.of(
                'GeneralAdministration',
              ),
              type: Report.Type.SubpoolAllocationsSummary,
            }),
            name: 'Summary of Allocation General Administration by Subpool',
          },
          {
            code: '10C',
            id: Report.Id.create({
              extra: {
                categoryId: FaFunction.Category.Standard.Id.of(
                  'GeneralAdministration',
                ),
              },
              type: Report.Type.AllocationSupport,
            }),
            name: 'General Administration Allocation Summary',
          },
        ] satisfies readonly Pick<
          ReportLabelEssentials,
          'code' | 'id' | 'name'
        >[]
      ).map<ReportLabelEssentials>(partialLabel => ({
        ...partialLabel,
        faFunctionCategorization: {
          categoryId: FaFunction.Category.Standard.Id.of(
            'GeneralAdministration',
          ),
        },
      })),
      /* ------------------------ Department Administration ----------------------- */
      ...(
        [
          {
            code: '12',
            id: Report.Id.create({
              extra: FaFunction.Category.Standard.Id.of(
                'DepartmentAdministration',
              ),
              type: Report.Type.Exchange,
            }),
            name: 'Allocation of Department Administration Cost Pool',
          },
          {
            code: '12A',
            id: Report.Id.create({
              extra: {
                categoryId: FaFunction.Category.Standard.Id.of(
                  'DepartmentAdministration',
                ),
                /**
                 * NOTE: the DA calculation is very complicated, so it is split
                 * into two reports. The difference between the reports is not
                 * exactly the {@link calculations.Allocation.ValueType}, but
                 * that is just a convenient way to identify them
                 */
                valueType: calculations.Allocation.ValueType.Currency,
              },
              type: Report.Type.AllocationSupport,
            }),
            name: 'Department Administration Summary by School',
          },
          {
            code: '12B',
            id: Report.Id.create({
              extra: {
                categoryId: FaFunction.Category.Standard.Id.of(
                  'DepartmentAdministration',
                ),
                /**
                 * NOTE: the DA calculation is very complicated, so it is split
                 * into two reports. The difference between the reports is not
                 * exactly the {@link calculations.Allocation.ValueType}, but
                 * that is just a convenient way to identify them
                 */
                valueType: calculations.Allocation.ValueType.Statistic,
              },
              type: Report.Type.AllocationSupport,
            }),
            name: 'Department Administration Worksheet',
          },
          {
            code: '12C',
            id: Report.Id.create({
              extra: FaFunction.Category.Standard.Id.of(
                'DepartmentAdministration',
              ),
              type: Report.Type.PoolSummary,
            }),
            name: "Dean's MTDC",
          },
        ] satisfies readonly Pick<
          ReportLabelEssentials,
          'code' | 'id' | 'name'
        >[]
      ).map<ReportLabelEssentials>(partialLabel => ({
        ...partialLabel,
        faFunctionCategorization: {
          categoryId: FaFunction.Category.Standard.Id.of(
            'DepartmentAdministration',
          ),
        },
      })),
      /* -------------------- Sponsored Project Administration -------------------- */
      ...(
        [
          {
            code: '13',
            id: Report.Id.create({
              extra: FaFunction.Category.Standard.Id.of(
                'SponsoredProjectsAdministration',
              ),
              type: Report.Type.Exchange,
            }),
            name: 'Allocation of Sponsored Projects Administration',
          },
          {
            code: '13A',
            id: Report.Id.create({
              extra: FaFunction.Category.Standard.Id.of(
                'SponsoredProjectsAdministration',
              ),
              type: Report.Type.PoolSummary,
            }),
            name: 'Summary of Sponsored Projects Administration Cost Pool',
          },
          {
            code: '13B',
            id: Report.Id.create({
              extra: {
                categoryId: FaFunction.Category.Standard.Id.of(
                  'SponsoredProjectsAdministration',
                ),
              },
              type: Report.Type.AllocationSupport,
            }),
            name: 'SPA Pool Allocation Statistics – Modified Total Costs of Sponsored Activities',
          },
        ] satisfies readonly Pick<
          ReportLabelEssentials,
          'code' | 'id' | 'name'
        >[]
      ).map<ReportLabelEssentials>(partialLabel => ({
        ...partialLabel,
        faFunctionCategorization: {
          categoryId: FaFunction.Category.Standard.Id.of(
            'SponsoredProjectsAdministration',
          ),
        },
      })),
      /* --------------------------------- Library -------------------------------- */
      ...(
        [
          {
            code: '14',
            id: Report.Id.create({
              extra: FaFunction.Category.Standard.Id.of('Library'),
              type: Report.Type.Exchange,
            }),
            name: 'Allocation of Library',
          },
          {
            code: '14A',
            id: Report.Id.create({
              extra: FaFunction.Category.Standard.Id.of('Library'),
              type: Report.Type.PoolSummary,
            }),
            name: 'Summary of Library Cost Pool',
          },
          {
            code: '14B',
            id: Report.Id.create({
              extra: {
                categoryId: FaFunction.Category.Standard.Id.of('Library'),
              },
              type: Report.Type.AllocationSupport,
            }),
            name: 'Library Allocation Summary',
          },
        ] satisfies readonly Pick<
          ReportLabelEssentials,
          'code' | 'id' | 'name'
        >[]
      ).map<ReportLabelEssentials>(partialLabel => ({
        ...partialLabel,
        faFunctionCategorization: {
          categoryId: FaFunction.Category.Standard.Id.of('Library'),
        },
      })),
      /* -------------------- Student Administration & Services ------------------- */
      ...(
        [
          {
            code: '15',
            id: Report.Id.create({
              extra: FaFunction.Category.Standard.Id.of(
                'StudentAdministrationAndServices',
              ),
              type: Report.Type.Exchange,
            }),
            name: 'Allocation of Student Administration & Services',
          },
          {
            code: '15A',
            id: Report.Id.create({
              extra: FaFunction.Category.Standard.Id.of(
                'StudentAdministrationAndServices',
              ),
              type: Report.Type.PoolSummary,
            }),
            name: 'Summary of Student Administration & Services Cost Pool',
          },
        ] satisfies readonly Pick<
          ReportLabelEssentials,
          'code' | 'id' | 'name'
        >[]
      ).map<ReportLabelEssentials>(partialLabel => ({
        ...partialLabel,
        faFunctionCategorization: {
          categoryId: FaFunction.Category.Standard.Id.of(
            'StudentAdministrationAndServices',
          ),
        },
      })),
    ] satisfies readonly ReportLabelEssentials[]
  ).map<Report.Label>((essentials, i) => ({
    ...essentials,
    // TODO: remove this. Instead, when we get reports without hints, we should
    // get the IDC category code based on the faFunctionCategorization. That way,
    // if the user changes the code in the DMS, we use the up-to-date code
    hint:
      essentials.hint ??
      (FaFunction.Category.Standard.Id.isId(
        essentials?.faFunctionCategorization?.categoryId,
      )
        ? FaFunction.Category.Standard.Code.from(
            essentials.faFunctionCategorization.categoryId,
          )
        : undefined),

    // Everything is active by default
    isActive: essentials?.isActive ?? true,
    // 1-based to be more user-friendly, in case it gets presented to the user
    position: i + 1,
  }))
}
export namespace getDefaultReportLabels {
  export interface Params {
    /**
     * For certain report types, we want a report label for each direct IDC
     * category. This is used to generate those report labels
     */
    directFaCategories: readonly Readonly<
      Pick<FaFunction.Category, 'id' | 'name'>
    >[]
  }
}

type ReportLabelEssentials = Partial.Semi<Report.Label, 'position' | 'isActive'>
